import React from "react";
import AssignmentChip from "./AssignmentChip";

const HQ_ROW_DND_TYPE = "application/x-medicalhq-extra-row";

function HQRow({
  iso,
  roleKey,
  roleLabel,
  displayLabel,
  assignment,
  blocked,
  employeeById,
  publishedShifts,
  readOnly,
  dragOver,
  unitText,
  isExtraSlot,
  onOpenAmbulanceEditor,
  onOpenAssign,
  onPublishedShiftClick,
  onEditAssignment,
  onDropAssignment,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onReorderExtraRow,
}) {
  const emp = assignment ? employeeById.get(assignment.employeeId) : null;
  const normalizedRoleLabel = String(roleLabel || "").trim();
  const normalizedDisplayLabel = String(displayLabel || "").trim();

  const isAmbulance =
    /^Ambulance\s*\(/i.test(normalizedRoleLabel) ||
    /^Ambulance\s+\d+/i.test(normalizedRoleLabel) ||
    /^Ambulance\s+\d+/i.test(normalizedDisplayLabel) ||
    /^Ambulance$/i.test(normalizedRoleLabel);

  const isTransport =
    /^Transport$/i.test(normalizedRoleLabel) ||
    /^Transport$/i.test(normalizedDisplayLabel);

  const isTriage =
    /^Triage Supervisor$/i.test(normalizedRoleLabel) ||
    /^Triage Sup$/i.test(normalizedDisplayLabel);

  const isStructuredUnitRow = isAmbulance || isTransport || isTriage;

  function getReorderSourceRoleKey(e) {
    try {
      return e.dataTransfer.getData(HQ_ROW_DND_TYPE) || "";
    } catch {
      return "";
    }
  }

  function isReorderDrag(e) {
    try {
      const types = Array.from(e.dataTransfer?.types || []);
      return types.includes(HQ_ROW_DND_TYPE);
    } catch {
      return false;
    }
  }

  function handleRowDragStart(e) {
    if (!isExtraSlot || readOnly) return;
    try {
      e.dataTransfer.setData(HQ_ROW_DND_TYPE, roleKey);
      e.dataTransfer.setData("text/plain", roleKey);
      e.dataTransfer.effectAllowed = "move";
    } catch {
      // ignore
    }
  }

  function handleRowDrop(e) {
    const sourceRoleKey = getReorderSourceRoleKey(e);
    if (!sourceRoleKey || sourceRoleKey === roleKey) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const placement = e.clientY < midpoint ? "before" : "after";

    onReorderExtraRow?.({
      sourceRoleKey,
      targetRoleKey: roleKey,
      placement,
    });
  }

  function renderContent() {
    if (blocked) {
      return (
        <button
          type="button"
          onClick={() => {
            if (!readOnly) onOpenAssign?.();
          }}
          disabled={readOnly}
          style={{
            fontSize: 11,
            color: "#64748b",
            background: "#f1f5f9",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            padding: "6px 8px",
            fontWeight: 700,
            textAlign: "left",
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          Blocked: {displayLabel || roleLabel}
        </button>
      );
    }

    if (isAmbulance) {
      return (
        <button
          type="button"
          onClick={onOpenAmbulanceEditor}
          disabled={readOnly}
          style={{
            fontSize: 11,
            color: unitText ? "#0f172a" : "#166534",
            background: dragOver ? "#d1fae5" : unitText ? "#f8fafc" : "#ecfdf5",
            border: dragOver
              ? "2px solid #2563eb"
              : unitText
              ? "1px solid #cbd5e1"
              : "1px solid #bbf7d0",
            borderRadius: 8,
            padding: "6px 8px",
            fontWeight: 700,
            textAlign: "left",
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          {unitText ? unitText : `Available: ${displayLabel || roleLabel}`}
        </button>
      );
    }

    if (assignment) {
      return (
        <AssignmentChip
          assignment={assignment}
          employee={emp}
          onOpen={() => onEditAssignment(assignment)}
          draggable={!isExtraSlot}
          onDragStart={isExtraSlot ? undefined : onDragStart}
          onDragEnd={isExtraSlot ? undefined : onDragEnd}
        />
      );
    }

    return (
      <div style={{ display: "grid", gap: 4 }}>
        {publishedShifts.length > 0 ? (
          publishedShifts.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!readOnly) onPublishedShiftClick(s);
              }}
              style={{
                fontSize: 11,
                color: "#166534",
                background: dragOver ? "#d1fae5" : "#ecfdf5",
                border: dragOver ? "2px solid #2563eb" : "1px solid #bbf7d0",
                borderRadius: 8,
                padding: "6px 8px",
                fontWeight: 700,
                textAlign: "left",
                cursor: readOnly ? "default" : "pointer",
              }}
            >
              Available: {s.position || displayLabel || roleLabel}
              {s.start && s.end ? ` • ${s.start}-${s.end}` : ""}
            </button>
          ))
        ) : (
          <button
            type="button"
            onClick={onOpenAssign}
            disabled={readOnly}
            style={{
              fontSize: 11,
              color: "#166534",
              background: dragOver ? "#d1fae5" : "#ecfdf5",
              border: dragOver ? "2px solid #2563eb" : "1px solid #bbf7d0",
              borderRadius: 8,
              padding: "6px 8px",
              fontWeight: 700,
              textAlign: "left",
              cursor: readOnly ? "default" : "pointer",
            }}
          >
            Available: {displayLabel || roleLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      draggable={isExtraSlot && !readOnly}
      onDragStart={handleRowDragStart}
      onDragOver={(e) => {
        if (isReorderDrag(e)) {
          e.preventDefault();
          return;
        }

        if (readOnly || blocked || isAmbulance) return;
        e.preventDefault();
      }}
      onDrop={(e) => {
        if (isReorderDrag(e)) {
          e.preventDefault();
          handleRowDrop(e);
          return;
        }

        if (readOnly || blocked || isAmbulance) return;
        e.preventDefault();
        onDropAssignment(e);
      }}
      onDragEnter={() => {
        if (!readOnly && !blocked && !isAmbulance) onDragEnter?.();
      }}
      onDragLeave={() => {
        if (!readOnly && !blocked && !isAmbulance) onDragLeave?.();
      }}
      style={{
        display: "grid",
        gap: 4,
        paddingTop: 6,
        borderTop: "1px solid #dbe7e3",
        cursor: isExtraSlot && !readOnly ? "grab" : "default",
      }}
    >
      {isStructuredUnitRow ? (
        <>
          <div
            style={{
              fontWeight: 800,
              fontSize: 11,
              color: "#0f172a",
              padding: "0 2px",
            }}
          >
            {displayLabel || roleLabel}
          </div>
          {renderContent()}
        </>
      ) : (
        renderContent()
      )}
    </div>
  );
}

export default function MedicalHQCard({
  iso,
  employeeById,
  dragOverKey,
  readOnly,
  rows,
  onReorderExtraRow,
}) {
  return (
    <div
      className="schedule-slot-card"
      style={{
        border: "1px solid #d8e1ec",
        borderRadius: 10,
        background: "#ffffff",
        padding: 8,
        display: "grid",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 8,
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 11, color: "#0f172a" }}>
          Medical HQ
        </div>
      </div>

      <div style={{ display: "grid", gap: 0 }}>
        {rows.map((row) => (
          <HQRow
            key={row.roleKey}
            iso={iso}
            roleKey={row.roleKey}
            roleLabel={row.roleLabel}
            displayLabel={row.displayLabel}
            assignment={row.assignment}
            blocked={row.blocked}
            employeeById={employeeById}
            publishedShifts={row.publishedShifts}
            readOnly={readOnly}
            dragOver={dragOverKey === `${iso}__${row.roleKey}`}
            unitText={row.unitText}
            isExtraSlot={!!row.isExtraSlot}
            onOpenAmbulanceEditor={row.onOpenAmbulanceEditor}
            onOpenAssign={row.onOpenAssign}
            onPublishedShiftClick={row.onPublishedShiftClick}
            onEditAssignment={row.onEditAssignment}
            onDropAssignment={row.onDropAssignment}
            onDragStart={row.onDragStart}
            onDragEnd={row.onDragEnd}
            onDragEnter={row.onDragEnter}
            onDragLeave={row.onDragLeave}
            onReorderExtraRow={onReorderExtraRow}
          />
        ))}
      </div>
    </div>
  );
}